import { useState, useMemo, useCallback } from 'react';
import { classify, QUESTIONS } from '@air/shared';

const STAGES = [
  'intro',
  'selfid',
  'sector',
  'size',
  'bought',
  'quiz',
  'expf',
  'gate',
  'regrouperConfirm',
  'report',
];

export function useAssessment() {
  const [stage, setStage] = useState('intro');
  const [selfId, setSelfId] = useState(null);
  const [sector, setSector] = useState(null);
  const [size, setSize] = useState(null);
  const [boughtTools, setBoughtTools] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [expFollowup, setExpFollowup] = useState(null);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [regrouperConfirmed, setRegrouperConfirmed] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const result = useMemo(
    () => classify({ answers, boughtTools, selfId, expFollowup }),
    [answers, boughtTools, selfId, expFollowup]
  );

  const answerQuestion = useCallback((val) => {
    const q = QUESTIONS[qIdx];
    const next = { ...answers, [q.id]: val };
    setAnswers(next);

    setTimeout(() => {
      if (qIdx < QUESTIONS.length - 1) {
        setQIdx(qIdx + 1);
      } else {
        // Decide whether the follow-up applies
        const provisional = classify({ answers: next, boughtTools, selfId, expFollowup: null });
        if (provisional.base === 'experimenter' || provisional.base === 'stalled') {
          setStage('expf');
        } else {
          setStage('gate');
        }
      }
    }, 200);
  }, [qIdx, answers, boughtTools, selfId]);

  const goBack = useCallback(() => {
    if (stage === 'quiz' && qIdx > 0) {
      setQIdx(qIdx - 1);
    } else {
      const currentIndex = STAGES.indexOf(stage);
      if (currentIndex > 0) {
        // Handle quiz specially
        if (stage === 'quiz') {
          setStage('bought');
        } else if (stage === 'bought') {
          setStage('size');
        } else if (stage === 'size') {
          setStage('sector');
        } else if (stage === 'sector') {
          setStage('selfid');
        } else if (stage === 'selfid') {
          setStage('intro');
        }
      }
    }
  }, [stage, qIdx]);

  const reset = useCallback(() => {
    setStage('intro');
    setSelfId(null);
    setSector(null);
    setSize(null);
    setBoughtTools(null);
    setQIdx(0);
    setAnswers({});
    setExpFollowup(null);
    setEmail('');
    setConsent(false);
    setRegrouperConfirmed(null);
    setAssessmentId(null);
    setIsSubmitting(false);
    setSubmitError(null);
  }, []);

  return {
    // State
    stage,
    selfId,
    sector,
    size,
    boughtTools,
    qIdx,
    answers,
    expFollowup,
    email,
    consent,
    regrouperConfirmed,
    assessmentId,
    isSubmitting,
    submitError,
    result,

    // Setters
    setStage,
    setSelfId,
    setSector,
    setSize,
    setBoughtTools,
    setQIdx,
    setAnswers,
    setExpFollowup,
    setEmail,
    setConsent,
    setRegrouperConfirmed,
    setAssessmentId,
    setIsSubmitting,
    setSubmitError,

    // Actions
    answerQuestion,
    goBack,
    reset,
  };
}
